import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchChatHistory, sendChatMessage } from "@/api/openai-api-v2";
import { format } from "date-fns";
import { useExtraDetails } from "@/hooks/useExtraDetails";
import { useTipsDisplay } from "@/hooks/useTipsDisplay";
import logger from "@/utils/logger";
import formatMessageContent from "@/utils/message-markup-formatter";
import { getStoredProductName } from "@/utils/product";
import { PRODUCT_LEXI_SOCIAL } from "@/utils/vars";
// import { formatMessage } from "@/utils/message-formatter";
import { Send } from "lucide-react";
import ChatInput from "./ChatInput";
import { getChatUserId } from "@/utils/chat-identity";

const messageStyles = `
  .numbered-list {
      flex-wrap: wrap;
  }
  .numbered-list:after {
    content: '';
    flex-basis: 100%;
  }
  .numbered-list, .bullet-list {
    margin: 4px 0;
    padding-left: 5px;
    display: flex;
    align-items: flex-start;
    gap: 4px;
  }
  
  .horse-reference {
    font-weight: 600;
    color: #F30099;
    display: inline-block;
  }
  
  strong {
    font-weight: 600;
    color: #333;
  }
  
  em {
    font-style: italic;
    color: inherit;
  }
  
  .bullet-list::before {
    content: "•";
    display: inline-block;
    margin-right: 4px;
    flex-shrink: 0;
  }
  
  .list-number {
    flex-shrink: 0;
    font-weight: 500;
  }
  
  /* Bouncing dots animation */
  .typing-indicator {
    display: inline-flex;
    align-items: center;
  }
  .typing-indicator span {
    height: 6px;
    width: 6px;
    background-color: #777;
    border-radius: 50%;
    display: inline-block;
    margin: 0 2px;
    opacity: 0.6;
  }
  .typing-indicator span:nth-child(1) {
    animation: bounce 1s infinite;
  }
  .typing-indicator span:nth-child(2) {
    animation: bounce 1s infinite .33s;
  }
  .typing-indicator span:nth-child(3) {
    animation: bounce 1s infinite .66s;
  }
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }
`;

// Create a new TypingIndicator component for the bouncing dots
const TypingIndicator = () => (
  <div className="typing-indicator">
    <span></span>
    <span></span>
    <span></span>
  </div>
);

export default function LexiChat({
  className = "",
  contextData = {},
  containerClassName = "",
  showHeader = false,
  analysis = "",
  earlyAnalysis = "",
  onNavigate = () => {},
  viewMode = "list",
  onCarouselPrevious = null,
  onCarouselNext = null,
  canCarouselPrevious = null,
  canCarouselNext = null,
  isLexiSocial: isLexiSocialProp = null,
}) {
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [currentTrackRace, setCurrentTrackRace] = useState(null);
  const [chatHistories, setChatHistories] = useState({});
  const [isTipHistoryExpanded, setIsTipHistoryExpanded] = useState(false);
  const [hasEarlyPredictions, setHasEarlyPredictions] = useState(false);
  const [earlyPredictionsData, setEarlyPredictionsData] = useState(null);
  const [streamingContent, setStreamingContent] = useState("");
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [showMessageContent, setShowMessageContent] = useState(false);
  const [productName, setProductName] = useState(
    () => getStoredProductName() || "",
  );
  const [readerRef, setReaderRef] = useState(null);

  const navigate = useNavigate();

  // Get tips display preference
  const { showAllTips } = useTipsDisplay();

  // show extra details
  const { showExtraDetails } = useExtraDetails();

  // Track if analysis has changed to avoid unnecessary API calls
  const analysisSignature = useMemo(() => {
    if (!contextData.track || !contextData.race) return null;
    return `${contextData.track}-${contextData.race}-${analysis ? "has-analysis" : "no-analysis"}-${earlyAnalysis ? "has-early" : "no-early"}`;
  }, [contextData.track, contextData.race, analysis, earlyAnalysis]);

  const isLexiSocial = useMemo(() => {
    if (typeof isLexiSocialProp === "boolean") {
      return isLexiSocialProp;
    }
    return (
      (productName || "").toLowerCase() === PRODUCT_LEXI_SOCIAL.toLowerCase()
    );
  }, [isLexiSocialProp, productName]);

  useEffect(() => {
    const handleProductChange = () => {
      try {
        setProductName(getStoredProductName() || "");
      } catch {
        setProductName("");
      }
    };

    window.addEventListener("lexi:product-updated", handleProductChange);
    window.addEventListener("storage", handleProductChange);

    return () => {
      window.removeEventListener("lexi:product-updated", handleProductChange);
      window.removeEventListener("storage", handleProductChange);
    };
  }, []);

  useEffect(() => {
    // The member's mobile, or their account ID when they have none
    const chatUserId = getChatUserId();
    if (chatUserId) {
      setUserId(chatUserId);
    }

    const style = document.createElement("style");
    style.textContent = messageStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Add delay before showing typing indicator when no messages
  useEffect(() => {
    if (messages.length === 0) {
      setShowTypingIndicator(false);
      setShowMessageContent(false);

      const typingTimer = setTimeout(() => {
        setShowTypingIndicator(true);

        const contentTimer = setTimeout(() => {
          setShowMessageContent(true);
        }, 2000); // 2 second delay after typing indicator

        return () => clearTimeout(contentTimer);
      }, 1000); // 1 second delay

      return () => clearTimeout(typingTimer);
    }
  }, [messages.length]);

  useEffect(() => {
    setMessages([]);
  }, [contextData.track, contextData.race]);

  // Deduplicate messages array to remove consecutive duplicates
  const deduplicateMessages = (messagesArray) => {
    if (!messagesArray || messagesArray.length <= 1) return messagesArray;

    const result = [messagesArray[0]];

    for (let i = 1; i < messagesArray.length; i++) {
      const current = messagesArray[i];
      const prev = result[result.length - 1];

      // Skip if this message is identical to the previous one
      if (current.role === prev.role && current.content === prev.content) {
        continue;
      }

      result.push(current);
    }

    return result;
  };

  // Effect to fetch chat history with deduplication
  useEffect(() => {
    if (isLexiSocial) {
      setMessages([]);
      setChatHistories({});
      setInput("");
      setIsLoading(false);
      return;
    }

    const newTrackRace = `${contextData.track}-${contextData.race}`;

    // If track or race has changed and we have a userId, fetch chat history
    const loadChatHistory = async () => {
      if (userId && contextData.track && contextData.race) {
        // Check if we're just switching to a track/race we've already loaded
        if (newTrackRace !== currentTrackRace && chatHistories[newTrackRace]) {
          // Use cached history but make sure it's deduplicated
          setMessages(deduplicateMessages(chatHistories[newTrackRace]));
          setCurrentTrackRace(newTrackRace);
          return;
        }

        setIsLoading(true);

        try {
          // Fetch chat history from Firestore
          const historyMessages = await fetchChatHistory(userId, {
            track: contextData.track,
            race: contextData.race,
          });

          if (historyMessages && historyMessages.length > 0) {
            // Process messages to clean up any "Context:" additions
            const cleanedMessages = historyMessages.map((msg) => {
              // Only process user messages that might contain the Context: suffix
              if (msg.role === "user") {
                const contextPattern = /\s*Context:\s*.*?Race\s*\d+\s*$/i;
                return {
                  ...msg,
                  content: msg.content.replace(contextPattern, ""),
                };
              }
              return msg;
            });

            // We have history from Firestore, so use it
            const historyWithAnalysis = [];

            // Only include early analysis if we don't have current analysis
            if (earlyAnalysis && !analysis && contextData.hasEarlyPredictions) {
              historyWithAnalysis.push({
                role: "assistant",
                content: `${earlyAnalysis}\n\n`,
                context: {
                  date: contextData.date,
                  hasHistoricalData: contextData.hasHistoricalData,
                  analysis: earlyAnalysis,
                  isEarly: true,
                },
              });
            }

            // Add current analysis if we have it
            if (analysis) {
              historyWithAnalysis.push({
                role: "assistant",
                content: `${analysis}\n\n`,
                context: {
                  date: contextData.date,
                  hasHistoricalData: contextData.hasHistoricalData,
                  analysis,
                  isEarly: false,
                },
              });
            }

            // Combine analysis with chat history and deduplicate
            const combinedMessages = deduplicateMessages([
              ...historyWithAnalysis,
              ...cleanedMessages,
            ]);

            setMessages(combinedMessages);
            setChatHistories((prev) => ({
              ...prev,
              [newTrackRace]: combinedMessages,
            }));
          } else {
            // No chat history found, proceed with normal initialization
            initializeWithAnalysis();
          }
        } catch (error) {
          logger.log("Error loading chat history:", error);
          // On error, fall back to normal initialization
          initializeWithAnalysis();
        } finally {
          setIsLoading(false);
        }
      } else {
        // No userId or track/race, use normal initialization
        // initializeWithAnalysis();
      }
    };

    // Helper function to initialize with just analysis (no history)
    const initializeWithAnalysis = () => {
      // Only proceed if we're actually changing races or if either analysis has changed
      if (
        (newTrackRace !== currentTrackRace ||
          analysis !== chatHistories[newTrackRace]?.[1]?.content ||
          earlyAnalysis !== chatHistories[newTrackRace]?.[0]?.content) &&
        (!chatHistories[newTrackRace] ||
          chatHistories[newTrackRace][1]?.content !== analysis ||
          chatHistories[newTrackRace][0]?.content !== earlyAnalysis)
      ) {
        let updatedMessages = [];

        // Only create and show messages if we have any analysis
        if (analysis || earlyAnalysis) {
          // Only include early analysis if we don't have current analysis
          const earlyAnalysisMessage =
            earlyAnalysis && !analysis && contextData.hasEarlyPredictions
              ? {
                  role: "assistant",
                  content: `${earlyAnalysis}\n\n`,
                  context: {
                    date: contextData.date,
                    hasHistoricalData: contextData.hasHistoricalData,
                    analysis: earlyAnalysis,
                    isEarly: true,
                  },
                }
              : null;

          // Create current analysis message
          const currentAnalysisMessage = analysis
            ? {
                role: "assistant",
                content: `${analysis}\n\n`,
                context: {
                  date: contextData.date,
                  hasHistoricalData: contextData.hasHistoricalData,
                  analysis,
                  isEarly: false,
                },
              }
            : null;

          // If we have existing chat history for this race, preserve the chronological order
          if (chatHistories[newTrackRace]) {
            // Get existing messages but filter out old analysis messages
            const existingMessages = chatHistories[newTrackRace].filter(
              (msg) =>
                msg.role === "user" ||
                (msg.role === "assistant" && !msg.context?.analysis),
            );

            // Add analysis messages at the start and deduplicate
            updatedMessages = deduplicateMessages([
              ...(earlyAnalysisMessage ? [earlyAnalysisMessage] : []),
              ...(currentAnalysisMessage ? [currentAnalysisMessage] : []),
              ...existingMessages,
            ]);
          } else {
            // No existing history, create new messages
            updatedMessages = [
              ...(earlyAnalysisMessage ? [earlyAnalysisMessage] : []),
              ...(currentAnalysisMessage ? [currentAnalysisMessage] : []),
            ];
          }

          // Set messages after a brief delay to show the loading ellipsis first
          setTimeout(() => {
            setMessages(updatedMessages);
            setCurrentTrackRace(newTrackRace);

            // Then update chat histories
            setChatHistories((prev) => ({
              ...prev,
              [newTrackRace]: updatedMessages,
            }));
          }, 800); // Short delay to show the loading ellipsis
        }
      } else if (newTrackRace !== currentTrackRace) {
        // If just switching to existing race conversation, load its messages
        setMessages(deduplicateMessages(chatHistories[newTrackRace] || []));
        setCurrentTrackRace(newTrackRace);
      }
    };

    loadChatHistory();
  }, [
    userId,
    analysisSignature,
    contextData.hasEarlyPredictions,
    isLexiSocial,
  ]);

  // Add effect to scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading || !userId || isLexiSocial) return;

    // Deduplicate - check if this exact message was just sent
    if (
      messages.length > 0 &&
      messages[messages.length - 1].role === "user" &&
      messages[messages.length - 1].content === input
    ) {
      logger.log("Preventing duplicate message submission");
      setInput("");
      return;
    }

    const userMessage = { role: "user", content: input };
    // Get existing messages without duplicating the analysis messages
    const existingMessages = chatHistories[currentTrackRace] || messages;
    const updatedMessages = [...existingMessages, userMessage];

    // Add a placeholder loading message with typing indicator
    const loadingMessage = {
      role: "assistant",
      content: "",
      isStreaming: true,
    };

    setMessages([...updatedMessages, loadingMessage]);
    setInput("");
    setIsLoading(true);

    try {
      // Always use the current context data
      const currentContext = {
        track: contextData.track || "",
        race: contextData.race || "",
        date: contextData.date,
        hasHistoricalData: contextData.hasHistoricalData,
        analysis: analysis,
      };

      // Only add context if this is the first human message for this race
      const isFirstHumanMessage =
        !chatHistories[currentTrackRace] ||
        !chatHistories[currentTrackRace].some((msg) => msg.role === "user");

      const messageContent = isFirstHumanMessage
        ? `${input} Context: ${contextData.track} Race ${contextData.race}`
        : input;

      // Get streaming response
      const streamResponse = await sendChatMessage(
        [{ role: "user", content: messageContent }],
        userId,
        currentContext,
      );

      if (!streamResponse) {
        throw new Error("No response from server");
      }

      // Create a new streaming message
      let fullContent = "";
      const reader = streamResponse.getReader();
      setReaderRef(reader);
      const decoder = new TextDecoder();

      // First check if we're dealing with a plain text response or a streamed object
      if (
        typeof streamResponse === "object" &&
        streamResponse.role &&
        streamResponse.content
      ) {
        // This is a plain object (fallback response), not a stream
        logger.log("Received plain object response:", streamResponse);

        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages];
          updatedMessages[updatedMessages.length - 1] = {
            role: "assistant",
            content: streamResponse.content,
            isStreaming: false,
          };
          return updatedMessages;
        });

        // Update chat history
        setChatHistories((prev) => ({
          ...prev,
          [currentTrackRace]: [...updatedMessages, streamResponse],
        }));

        return; // Exit early since we've handled the response
      }

      // Update the placeholder message to prepare for streaming content
      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages];
        updatedMessages[updatedMessages.length - 1] = {
          role: "assistant",
          content: "",
          isStreaming: true,
        };
        return updatedMessages;
      });

      // Try to read the stream line by line
      let done = false;
      let buffer = "";
      let hasStartedActualStreaming = false;
      let initialMessageShown = false;

      // Variables to store the initial message
      let initialMessageContent =
        "I'll check the specific tip for this race...";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          // Convert the chunk to text
          const chunk = decoder.decode(value, { stream: true });
          logger.log("Raw chunk:", chunk);

          // Look for data: prefix pattern which is what we're seeing in the response
          const dataLines = chunk
            .split("\n")
            .filter((line) => line.trim())
            .map((line) => {
              // Check if the line starts with "data: "
              const match = line.match(/^data:\s*(.*)/);
              if (match) {
                return match[1]; // Extract the JSON part after "data: "
              }
              return line; // Keep the line as is if no match
            });

          for (const line of dataLines) {
            try {
              // Try to parse as JSON
              const json = JSON.parse(line);

              // Handle token type
              if (json.type === "token") {
                // Check if this is the initial complete message
                // Look for patterns like sentences with "I'll" or "checking" or "for this race" that indicate initial messages
                const isLikelyInitialMessage =
                  json.token &&
                  (json.token.includes("I'll") ||
                    json.token.includes("checking") ||
                    json.token.includes("for this race") ||
                    json.token.includes("Let me")) &&
                  json.token.length > 10;

                if (!initialMessageShown && isLikelyInitialMessage) {
                  logger.log("Found initial message:", json.token);
                  initialMessageShown = true;
                  initialMessageContent = json.token; // Store the actual initial message

                  // Show this initial message
                  setMessages((prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    updatedMessages[updatedMessages.length - 1] = {
                      role: "assistant",
                      content: json.token,
                      isStreaming: true,
                      isInitialMessage: true,
                    };
                    return updatedMessages;
                  });

                  // Add a slight delay before clearing for streaming to begin
                  setTimeout(() => {
                    if (!hasStartedActualStreaming) {
                      fullContent = ""; // Reset content for actual streaming

                      // Clear the initial message
                      setMessages((prevMessages) => {
                        const updatedMessages = [...prevMessages];
                        updatedMessages[updatedMessages.length - 1] = {
                          role: "assistant",
                          content: "",
                          isStreaming: true,
                        };
                        return updatedMessages;
                      });
                    }
                  }, 3000); // 3 second delay
                }
                // For subsequent tokens (after initial message)
                else if (json.token !== undefined) {
                  // If this is the first actual token after initial message
                  if (
                    initialMessageShown &&
                    !hasStartedActualStreaming &&
                    json.token.trim()
                  ) {
                    hasStartedActualStreaming = true;
                    fullContent = ""; // Reset the content for actual streaming
                  }

                  fullContent += json.token;

                  // Update the message
                  if (hasStartedActualStreaming || !initialMessageShown) {
                    setMessages((prevMessages) => {
                      const updatedMessages = [...prevMessages];
                      updatedMessages[updatedMessages.length - 1] = {
                        role: "assistant",
                        content: fullContent,
                        isStreaming: true,
                      };
                      return updatedMessages;
                    });
                  }
                }
              }
              // Handle status type
              else if (json.type === "status") {
                logger.log("Stream status:", json.status);
              }
            } catch (e) {
              // If JSON parsing fails and we have text, add it to buffer
              if (line.trim()) {
                logger.log("Non-JSON line:", line);
                buffer += line + "\n";
              }
            }
          }

          // If we couldn't handle as JSON, try as plain text - but only in special cases
          // Usually we don't want to display raw text from the buffer to avoid showing JSON
          if (
            buffer.trim() &&
            !dataLines.some((line) => line.includes('"type":'))
          ) {
            logger.log("Processing buffer as plain text:", buffer);
            fullContent += buffer;
            buffer = "";

            // Update the message with the plain text
            setMessages((prevMessages) => {
              const updatedMessages = [...prevMessages];
              updatedMessages[updatedMessages.length - 1] = {
                role: "assistant",
                content: fullContent,
                isStreaming: true,
              };
              return updatedMessages;
            });
          }
        }
      }

      // Clean up the message content before displaying the final message - remove any JSON artifacts
      let cleanContent = fullContent
        .replace(/data:\s*\{"type":"[^"]+","[^"]+":"[^"]*"\}/g, "") // Remove data: {"type":"token","token":""} patterns
        .replace(/\{"type":"[^"]+","[^"]+":"[^"]*"\}/g, "") // Remove {"type":"token","token":""} patterns
        .replace(/\s+/g, " ") // Normalize whitespace
        .trim();

      // If we only showed the initial message and never got to actual streaming, use that message
      if (initialMessageShown && !hasStartedActualStreaming) {
        cleanContent = initialMessageContent;
      }

      // If content is still empty but we had an initial message, use that as fallback
      if (!cleanContent && initialMessageShown) {
        cleanContent = initialMessageContent;
      }

      // Finish streaming - update the message to final state
      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages];
        updatedMessages[updatedMessages.length - 1] = {
          role: "assistant",
          content:
            cleanContent ||
            "I received your message but couldn't generate content.",
          isStreaming: false,
        };
        return updatedMessages;
      });

      // Update chat history with the final message
      const assistantMessage = {
        role: "assistant",
        content:
          cleanContent ||
          "I received your message but couldn't generate content.",
      };

      // Update chat histories - ensuring we don't add duplicate messages
      setChatHistories((prev) => {
        const existingHistory = prev[currentTrackRace] || [];

        // Check if the last message in history is identical to prevent duplicates
        if (
          existingHistory.length > 0 &&
          existingHistory[existingHistory.length - 1].role === "assistant" &&
          existingHistory[existingHistory.length - 1].content ===
            assistantMessage.content
        ) {
          logger.log("Preventing duplicate assistant message in history");
          return prev; // Don't update if it would create a duplicate
        }

        // Also check if there's an identical user message duplicated
        const deduplicatedMessages = [...updatedMessages];
        let lastUserMessageIndex = -1;

        // Find duplicated user messages
        for (let i = deduplicatedMessages.length - 1; i >= 0; i--) {
          if (deduplicatedMessages[i].role === "user") {
            if (
              lastUserMessageIndex >= 0 &&
              deduplicatedMessages[i].content ===
                deduplicatedMessages[lastUserMessageIndex].content
            ) {
              // Found a duplicate user message
              deduplicatedMessages.splice(i, 1);
            } else {
              lastUserMessageIndex = i;
            }
          }
        }

        return {
          ...prev,
          [currentTrackRace]: [...deduplicatedMessages, assistantMessage],
        };
      });
    } catch (error) {
      logger.error("Error in chat:", error);
      const errorMessage = {
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
      };
      setMessages([...updatedMessages, errorMessage]);
      setChatHistories((prev) => ({
        ...prev,
        [currentTrackRace]: [...updatedMessages, errorMessage],
      }));
    } finally {
      setIsLoading(false);
      if (readerRef) {
        readerRef.cancel().catch(logger.error);
        setReaderRef(null);
      }
    }
  };

  // Clean up stream on unmount
  useEffect(() => {
    return () => {
      if (readerRef) {
        readerRef.cancel().catch(logger.error);
      }
    };
  }, [readerRef]);

  // Render formatted message
  const renderFormattedMessage = (content) => {
    const formattedContent = formatMessageContent(content);
    return <div dangerouslySetInnerHTML={{ __html: formattedContent }} />;
  };

  const handlePrevious = () => {
    // Existing navigation logic
    const currentRaceIndex = contextData.races?.indexOf(
      parseInt(contextData.race),
    );
    const currentTrackIndex = contextData.tracks?.indexOf(contextData.track);

    let newTrack, newRace;

    if (currentRaceIndex > 0) {
      // Go to previous race in current track
      newTrack = contextData.track;
      newRace = contextData.races[currentRaceIndex - 1].toString();
    } else if (currentTrackIndex > 0) {
      // Go to last race of previous track
      newTrack = contextData.tracks[currentTrackIndex - 1];
      const lastRaceOfPreviousTrack =
        contextData.trackMap?.get(newTrack)?.size || 1;
      newRace = lastRaceOfPreviousTrack.toString();
    } else {
      // Wrap around to last track and its last race
      newTrack = contextData.tracks[contextData.tracks.length - 1];
      const lastRaceOfLastTrack =
        contextData.trackMap?.get(newTrack)?.size || 1;
      newRace = lastRaceOfLastTrack.toString();
    }

    // Update URL with query parameters without page reload
    const formattedTrack = newTrack.replace(/\s+/g, "-").toLowerCase();
    const url = new URL(window.location.href);
    url.searchParams.set("track", formattedTrack);
    url.searchParams.set("race", newRace);
    window.history.pushState({}, "", url);

    // Dispatch custom event to notify components about the URL change
    window.dispatchEvent(
      new CustomEvent("urlchange", {
        detail: { track: formattedTrack, race: newRace },
      }),
    );

    // Call onNavigate to update the parent component's state
    onNavigate(newTrack, newRace);
  };

  const handleNext = () => {
    // Use carousel navigation if provided, otherwise fall back to existing logic
    // if (onCarouselNext) {
    //   onCarouselNext();
    //   return;
    // }

    // Existing navigation logic
    const currentRaceIndex = contextData.races?.indexOf(
      parseInt(contextData.race),
    );
    const currentTrackIndex = contextData.tracks?.indexOf(contextData.track);

    let newTrack, newRace;

    if (currentRaceIndex < (contextData.races?.length || 0) - 1) {
      // Go to next race in current track
      newTrack = contextData.track;
      newRace = contextData.races[currentRaceIndex + 1].toString();
    } else if (currentTrackIndex < (contextData.tracks?.length || 0) - 1) {
      // Go to first race of next track
      newTrack = contextData.tracks[currentTrackIndex + 1];
      newRace = "1";
    } else {
      // Wrap around to first track and its first race
      newTrack = contextData.tracks[0];
      newRace = "1";
    }

    // Update URL with query parameters without page reload
    const formattedTrack = newTrack.replace(/\s+/g, "-").toLowerCase();
    const url = new URL(window.location.href);
    url.searchParams.set("track", formattedTrack);
    url.searchParams.set("race", newRace);
    window.history.pushState({}, "", url);

    // Dispatch custom event to notify components about the URL change
    window.dispatchEvent(
      new CustomEvent("urlchange", {
        detail: { track: formattedTrack, race: newRace },
      }),
    );

    // Call onNavigate to update the parent component's state
    onNavigate(newTrack, newRace);
  };

  const canGoPrevious = () => {
    // Use carousel navigation check if provided, otherwise fall back to existing logic
    if (typeof canCarouselPrevious === "function") {
      return canCarouselPrevious();
    }
    return contextData.tracks?.length > 0 && contextData.races?.length > 0;
  };

  const canGoNext = () => {
    // Use carousel navigation check if provided, otherwise fall back to existing logic
    if (typeof canCarouselNext === "function") {
      return canCarouselNext();
    }
    return contextData.tracks?.length > 0 && contextData.races?.length > 0;
  };

  // Add effect to check for early predictions
  useEffect(() => {
    const checkEarlyPredictions = () => {
      if (!contextData.earlyPredictions) return;

      const raceKey = `${contextData.track?.replace(/\s+/g, "-")}-${contextData.race}`;
      const earlyData = contextData.earlyPredictions[raceKey];

      if (
        earlyData?.firstFourData &&
        Array.isArray(earlyData.firstFourData) &&
        earlyData.firstFourData.length > 0
      ) {
        setHasEarlyPredictions(true);
        setEarlyPredictionsData(earlyData);
      } else {
        setHasEarlyPredictions(false);
        setEarlyPredictionsData(null);
      }
    };

    checkEarlyPredictions();
  }, [contextData.track, contextData.race, contextData.earlyPredictions]);

  // If no userId is available and chat isn't locked, show a message or redirect to login
  if (!userId && !isLexiSocial) {
    return (
      <div className="flex flex-col items-center justify-center p-4 text-gray-500">
        <p>Please log in to chat with Lexi</p>
      </div>
    );
  }

  const isChatDisabled = isLexiSocial;

  return (
    <div className={`flex flex-col ${className}`}>
      {showHeader && (
        <div className="flex-none px-4 py-6">
          <h1 className="text-xl font-extrabold">Ask Lexi</h1>
        </div>
      )}

      <div
        className={`chat-container-inner flex-1 overflow-y-auto px-4 ${containerClassName}`}
      >
        <div
          className={`space-y-4 ${
            viewMode === "dial"
              ? "pt-[390px] pb-4"
              : showAllTips
                ? "pt-[310px] pb-[100px]" // Increased padding for 4 tips
                : showExtraDetails
                  ? "pt-[320px] pb-[120px]"
                  : "pt-[210px] pb-[110px]" // Original padding for 2 tips
          }`}
        >
          {isChatDisabled ? (
            <div className="flex items-center justify-center pt-28">
              <div className="w-full text-center text-sm font-semibold text-gray-600 bg-gray-50 border border-dashed border-gray-200 rounded-xl px-4 py-6">
                LexiChat available with a Lexi Pro subscription
              </div>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={index}
                className={`flex lexi-chat-bubble ${
                  message.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[80%] ${
                    message.role === "user"
                      ? "bg-white text-black rounded-[20px] rounded-br-none"
                      : "bg-[#ffe6f6] text-black rounded-[20px] rounded-bl-none"
                  } px-4 py-3 shadow-[0_2px_4px_rgba(0,0,0,0.2)]`}
                >
                  <span
                    className={`font-bold text-[#F30099] block mb-1 ${
                      message.role === "user"
                        ? "text-xs text-right"
                        : "text-sm text-left italic"
                    }`}
                  >
                    {message.role === "assistant" ? "Lexi" : null}
                  </span>
                  <div className="text-[14px] leading-[1.3]">
                    {message.isStreaming && message.content === "" ? (
                      <TypingIndicator />
                    ) : (showTypingIndicator &&
                        !showMessageContent &&
                        index === 0) ||
                      message.isLoading ? (
                      <TypingIndicator />
                    ) : (
                      (showMessageContent || index > 0) &&
                      renderFormattedMessage(message.content)
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <ChatInput
        input={input}
        setInput={setInput}
        handleSubmit={handleSubmit}
        isLoading={isLoading}
        onPrevious={handlePrevious}
        onNext={handleNext}
        canGoPrevious={canGoPrevious()}
        canGoNext={canGoNext()}
        isDisabled={isChatDisabled}
      />
    </div>
  );
}
